import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Brand } from "./Brand";
import { WhatsAppIcon } from "./icons";
import { scrollToId, lockScroll } from "../lib/scroll";
import { SITE, waLink } from "../lib/site";

const NAV_LEFT = [
  { id: "gallery", label: "Our Rugs" },
  { id: "how-it-works", label: "How It Works" },
];

const NAV_RIGHT = [
  { id: "customise", label: "Customise" },
  { id: "faqs", label: "FAQs" },
];

const NAV = [...NAV_LEFT, ...NAV_RIGHT];

export function Header() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    lockScroll(open);
    return () => lockScroll(false);
  }, [open]);

  const go = (id: string) => {
    setOpen(false);
    setTimeout(() => scrollToId(id), 60);
  };

  return (
    <>
      <header className="sticky top-0 z-50 bg-[#002413] border-b border-brass/25">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 h-16 md:h-[76px] grid grid-cols-[1fr_auto_1fr] items-center gap-3">
          <div className="flex items-center gap-2">
            <button
              className="md:hidden inline-flex items-center justify-center h-10 w-10 rounded-full border border-ivory/25 text-ivory"
              onClick={() => setOpen(true)}
              aria-label="Open menu"
              aria-expanded={open}
              data-testid="menu-open-button"
            >
              <Menu className="h-5 w-5" />
            </button>
            <nav className="hidden md:flex items-center gap-7" aria-label="Primary">
              {NAV_LEFT.map((n) => (
                <button
                  key={n.id}
                  onClick={() => go(n.id)}
                  data-testid={`nav-${n.id}`}
                  className="text-sm font-medium text-ivory/80 hover:text-ivory transition-colors duration-200"
                >
                  {n.label}
                </button>
              ))}
            </nav>
          </div>

          <a
            href="/"
            className="shrink-0 justify-self-center"
            onClick={(e) => {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            aria-label="Tuft and Tale — home"
          >
            <Brand />
          </a>

          <div className="flex items-center justify-end gap-2">
            <nav className="hidden md:flex items-center gap-7" aria-label="Secondary">
              {NAV_RIGHT.map((n) => (
                <button
                  key={n.id}
                  onClick={() => go(n.id)}
                  data-testid={`nav-${n.id}`}
                  className="text-sm font-medium text-ivory/80 hover:text-ivory transition-colors duration-200"
                >
                  {n.label}
                </button>
              ))}
            </nav>
            <a
              href={waLink(SITE.waGenericMessage)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="whatsapp-header-button"
              className="hidden sm:inline-flex items-center gap-2 rounded-full bg-ivory text-forest text-sm font-medium px-5 py-2.5 transition-all duration-200 hover:bg-brass-soft hover:-translate-y-0.5 shadow-tuft"
            >
              <WhatsAppIcon />
              Enquire on WhatsApp
            </a>
            <a
              href={waLink(SITE.waGenericMessage)}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Enquire on WhatsApp"
              data-testid="whatsapp-header-icon-button"
              className="sm:hidden inline-flex items-center justify-center h-10 w-10 rounded-full bg-ivory text-forest"
            >
              <WhatsAppIcon className="h-5 w-5" />
            </a>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[60] bg-ivory texture-weave flex flex-col"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            data-testid="mobile-menu"
          >
            <div className="max-w-6xl w-full mx-auto px-5 h-16 flex items-center justify-between">
              <Brand />
              <button
                className="inline-flex items-center justify-center h-10 w-10 rounded-full border border-forest/15 text-forest"
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                data-testid="menu-close-button"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center gap-2 px-8" aria-label="Mobile">
              {NAV.map((n, i) => (
                <motion.button
                  key={n.id}
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                  onClick={() => go(n.id)}
                  className="text-left font-serif text-4xl text-forest py-3 border-b border-brass/30"
                  data-testid={`mobile-nav-${n.id}`}
                >
                  {n.label}
                </motion.button>
              ))}
            </nav>
            <div className="px-8 pb-10">
              <a
                href={waLink(SITE.waGenericMessage)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="whatsapp-mobile-menu-button"
                className="flex items-center justify-center gap-2 rounded-full bg-forest text-ivory font-medium px-6 py-4"
              >
                <WhatsAppIcon />
                Enquire on WhatsApp
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
